import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, ArrowUpRight, Instagram } from "lucide-react";
import Logo from "./Logo";
import { CONTACT } from "../data/site";

export default function Footer() {
  return (
    <footer data-testid="footer" className="relative bg-navy text-ivory overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 pt-24 md:pt-32 pb-12">
        {/* huge wordmark */}
        <div className="border-b border-ivory/15 pb-16">
          <p className="overline text-gold">Vista Banquet Hall of Alexandria</p>
          <h2 className="mt-6 font-serif text-5xl sm:text-7xl md:text-[8rem] font-light leading-[0.9] tracking-tight">
            Capturing <span className="italic text-gold">Elegance</span>,
            <br /> Crafting Memories.
          </h2>
        </div>

        <div className="grid gap-12 py-16 md:grid-cols-12">
          <div className="md:col-span-4">
            <Logo light />
            <p className="mt-6 max-w-xs text-ivory/60 font-light leading-relaxed">
              An elegant and modern wedding & events venue in the heart of Alexandria, Virginia.
            </p>
          </div>

          <div className="md:col-span-4">
            <p className="overline text-gold mb-6">Visit Us</p>
            <ul className="space-y-5 text-ivory/80 font-light">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="mt-1 text-gold shrink-0" />
                <a href={CONTACT.mapUrl} target="_blank" rel="noreferrer" data-testid="footer-address" className="hover:text-gold transition-colors">
                  {CONTACT.addressLine1}<br />{CONTACT.addressLine2}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Phone size={18} className="text-gold shrink-0" />
                <a href={CONTACT.phoneHref} data-testid="footer-phone" className="hover:text-gold transition-colors">{CONTACT.phone}</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={18} className="text-gold shrink-0" />
                <a href={`mailto:${CONTACT.email}`} data-testid="footer-email" className="hover:text-gold transition-colors">{CONTACT.email}</a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="overline text-gold mb-6">Explore</p>
            <div className="grid grid-cols-2 gap-y-4 gap-x-6 font-light">
              {[
                { to: "/", label: "Home" },
                { to: "/pricing", label: "Pricing" },
                { to: "/tour", label: "Request a Tour" },
                { to: "/contact", label: "Contact" },
                { to: "/careers", label: "Careers" },
              ].map((l) => (
                <Link key={l.to} to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-ivory/80 hover:text-gold transition-colors">
                  {l.label}
                </Link>
              ))}
            </div>
            <p className="overline text-gold mt-10 mb-4">Featured On</p>
            <div className="flex flex-col gap-3 font-light">
              <a href={CONTACT.theKnot} target="_blank" rel="noreferrer" data-testid="footer-theknot" className="group inline-flex items-center gap-1 text-ivory/80 hover:text-gold transition-colors">
                The Knot <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a href={CONTACT.weddingWire} target="_blank" rel="noreferrer" data-testid="footer-weddingwire" className="group inline-flex items-center gap-1 text-ivory/80 hover:text-gold transition-colors">
                WeddingWire <ArrowUpRight size={14} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <a href={CONTACT.instagram} target="_blank" rel="noreferrer" data-testid="footer-instagram" className="group inline-flex items-center gap-2 text-ivory/80 hover:text-gold transition-colors">
                <Instagram size={15} /> Instagram
              </a>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-ivory/15 pt-8 text-xs text-ivory/50 md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} Vista Banquet Hall of Alexandria. All rights reserved.</p>
          <p className="font-serif italic text-base text-ivory/70">Capturing Elegance, Crafting Memories</p>
          <p>
            Website & SEO by{" "}
            <a
              href="https://webnehtech.com"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-credit-webnehtech"
              className="text-ivory/70 hover:text-gold transition-colors"
            >
              WebNehTech
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
